import React, { useState, useEffect, useRef, useCallback } from "react";
import { Container, Row, Col, Card, Form, Button, Spinner, Dropdown, Table, InputGroup, FormControl, Modal } from "react-bootstrap";
import ForceGraph2D from "react-force-graph-2d";
import { collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import "./DatabasePage.css";

const DatabasePage = () => {
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [highlightNodes, setHighlightNodes] = useState(new Set());
  const [highlightLinks, setHighlightLinks] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);
  const [filters, setFilters] = useState({
    parent_category: true,
    agent: true,
    dark_pattern: true,
    law: true,
    jurisdiction: true,
    clause: true
  });
  const [filteredData, setFilteredData] = useState({ nodes: [], links: [] });
  
  // New state variables for database selection and reasoning database
  const [selectedDatabase, setSelectedDatabase] = useState("regulatory");
  const [reasoningData, setReasoningData] = useState([]);
  const [reasoningLoading, setReasoningLoading] = useState(false);
  const [sessionFilter, setSessionFilter] = useState("");
  const [journeyFilter, setJourneyFilter] = useState("");
  const [sortField, setSortField] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // New state for response detail modal
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  
  // Add these state variables for dropdown filters
  const [availableSessions, setAvailableSessions] = useState([]);
  const [availableJourneys, setAvailableJourneys] = useState([]);
  
  const graphRef = useRef();

  const NODE_COLORS = {
    parent_category: "#e41a1c", // red
    agent: "#377eb8",           // blue
    dark_pattern: "#4daf4a",    // green
    law: "#984ea3",             // purple
    jurisdiction: "#ff7f00",    // orange
    clause: "#ffff33"           // yellow
  };

  const NODE_SIZES = {
    parent_category: 12,
    agent: 8,
    dark_pattern: 10,
    law: 9,
    jurisdiction: 11,
    clause: 7
  };

  // Load the graph data from the JSON file
  useEffect(() => {
    if (selectedDatabase === "regulatory") {
      const loadGraphData = async () => {
        try {
          setLoading(true);
          // In a real app, this would be a fetch to your API endpoint
          // For now we'll use a mock response structure
          const response = await fetch("/data/regulatory_graph_data.json");
          const data = await response.json();

          // Transform data if needed
          const transformedData = {
            nodes: data.nodes.map(node => ({
              id: node.id,
              group: node.type || "unknown",
              label: node.id
            })),
            links: data.edges.map(edge => ({
              source: edge.source,
              target: edge.target
            }))
          };

          setGraphData(transformedData);
          setFilteredData(transformedData);
          setLoading(false);
        } catch (error) {
          console.error("Error loading graph data:", error);
          // Fallback to sample data
          const sampleData = {
            nodes: [
              { id: "Node 1", group: "parent_category", label: "Node 1" },
              { id: "Node 2", group: "agent", label: "Node 2" },
              { id: "Node 3", group: "dark_pattern", label: "Node 3" },
              { id: "Node 4", group: "law", label: "Node 4" },
              { id: "Node 5", group: "jurisdiction", label: "Node 5" },
              { id: "Node 6", group: "clause", label: "Node 6" }
            ],
            links: [
              { source: "Node 1", target: "Node 2" },
              { source: "Node 1", target: "Node 3" },
              { source: "Node 2", target: "Node 4" },
              { source: "Node 3", target: "Node 5" },
              { source: "Node 5", target: "Node 6" }
            ]
          };
          setGraphData(sampleData);
          setFilteredData(sampleData);
          setLoading(false);
        }
      };

      loadGraphData();
    }
  }, [selectedDatabase]);

  // Load reasoning database data
  useEffect(() => {
    if (selectedDatabase === "reasoning") {
      const loadReasoningData = async () => {
        try {
          setReasoningLoading(true);
          const reasoningRef = collection(db, "Reasoning Database");
          const snapshot = await getDocs(reasoningRef);
          
          const reasoningEntries = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Convert timestamp to Date object if it exists
            timestamp: doc.data().timestamp?.toDate() || new Date()
          }));
          
          setReasoningData(reasoningEntries);
          
          // Extract unique session IDs and journey IDs for dropdowns
          const sessions = [...new Set(reasoningEntries.map(entry => entry.session_id))].filter(Boolean);
          const journeys = [...new Set(reasoningEntries.map(entry => entry.journey_id))].filter(Boolean);
          
          setAvailableSessions(sessions);
          setAvailableJourneys(journeys);
          
          setReasoningLoading(false);
        } catch (error) {
          console.error("Error loading reasoning database:", error);
          setReasoningData([]);
          setReasoningLoading(false);
        }
      };

      loadReasoningData();
    }
  }, [selectedDatabase]);

  // Apply filters whenever the filter state changes
  useEffect(() => {
    if (graphData.nodes.length > 0) {
      // Filter nodes based on filter settings
      const visibleNodes = graphData.nodes.filter(node => filters[node.group]);
      const visibleNodeIds = new Set(visibleNodes.map(node => node.id));

      // Keep only links where both source and target nodes are visible
      const visibleLinks = graphData.links.filter(link => 
        visibleNodeIds.has(link.source.id || link.source) && 
        visibleNodeIds.has(link.target.id || link.target)
      );

      setFilteredData({
        nodes: visibleNodes,
        links: visibleLinks
      });

      // Reset selected node if it's no longer visible
      if (selectedNode && !visibleNodeIds.has(selectedNode.id)) {
        setSelectedNode(null);
        setHighlightNodes(new Set());
        setHighlightLinks(new Set());
      }
    }
  }, [filters, graphData]);

  // Handle filter changes
  const handleFilterChange = (filterType) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }));
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      parent_category: true,
      agent: true,
      dark_pattern: true,
      law: true,
      jurisdiction: true,
      clause: true
    });
    setSelectedNode(null);
    setHighlightNodes(new Set());
    setHighlightLinks(new Set());
    
    // Reset the graph view
    if (graphRef.current) {
      graphRef.current.zoomToFit(400);
    }
  };

  // Node painting
  const paintNode = useCallback((node, ctx) => {
    const size = NODE_SIZES[node.group] || 5;
    const color = NODE_COLORS[node.group] || "#cccccc";
    
    // Draw circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
    
    // Add border if highlighted
    if (highlightNodes.has(node.id)) {
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    
    // Add label with background
    const label = node.label || node.id;
    if (label) {
      ctx.font = '4px Arial';
      const textWidth = ctx.measureText(label).width;
      
      // Label background
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(node.x + size + 1, node.y - 3, textWidth + 4, 6);
      
      // Label text
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, node.x + size + 3, node.y);
    }
  }, [highlightNodes]);

  // Link painting
  const paintLink = useCallback((link, ctx) => {
    ctx.beginPath();
    ctx.moveTo(link.source.x, link.source.y);
    ctx.lineTo(link.target.x, link.target.y);
    
    // Highlight the link if it's selected
    if (highlightLinks.has(link)) {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
    } else {
      ctx.strokeStyle = '#999999';
      ctx.lineWidth = 0.5;
    }
    
    ctx.stroke();
  }, [highlightLinks]);

  // Handle node click
  const handleNodeClick = (node) => {
    if (selectedNode === node) {
      // Deselect if clicking the same node
      setSelectedNode(null);
      setHighlightNodes(new Set());
      setHighlightLinks(new Set());
      return;
    }
    
    setSelectedNode(node);
    
    // Get all connected links and nodes
    const connectedLinks = filteredData.links.filter(link => 
      link.source.id === node.id || link.source === node.id || 
      link.target.id === node.id || link.target === node.id
    );
    
    const connectedNodes = new Set();
    connectedNodes.add(node.id);
    
    connectedLinks.forEach(link => {
      const sourceId = link.source.id || link.source;
      const targetId = link.target.id || link.target;
      connectedNodes.add(sourceId);
      connectedNodes.add(targetId);
    });
    
    setHighlightLinks(new Set(connectedLinks));
    setHighlightNodes(connectedNodes);
  };

  // Focus the graph
  const focusGraph = () => {
    if (graphRef.current) {
      graphRef.current.zoomToFit(400);
    }
  };

  // Handle database selection change
  const handleDatabaseChange = (dbType) => {
    setSelectedDatabase(dbType);
    setSelectedNode(null);
    setHighlightNodes(new Set());
    setHighlightLinks(new Set());
  };

  // Handle reasoning database entry deletion
  const handleDeleteEntry = async (entryId) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      try {
        await deleteDoc(doc(db, "Reasoning Database", entryId));
        setReasoningData(prevData => prevData.filter(entry => entry.id !== entryId));
        alert("Entry deleted successfully");
      } catch (error) {
        console.error("Error deleting entry:", error);
        alert("Failed to delete entry: " + error.message);
      }
    }
  };

  // Filter and sort reasoning data
  const filteredReasoningData = reasoningData
    .filter(entry => 
      (sessionFilter === "" || entry.session_id?.includes(sessionFilter)) &&
      (journeyFilter === "" || entry.journey_id?.includes(journeyFilter))
    )
    .sort((a, b) => {
      // Handle sorting based on the selected field and direction
      if (sortField === "timestamp") {
        return sortDirection === "asc" 
          ? a.timestamp - b.timestamp 
          : b.timestamp - a.timestamp;
      } else {
        const valueA = String(a[sortField] || "").toLowerCase();
        const valueB = String(b[sortField] || "").toLowerCase();
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
    });

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortDirection(prevDirection => prevDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Open response detail modal
  const handleViewResponse = (response) => {
    setSelectedResponse(response);
    setShowResponseModal(true);
  };

  // Close response detail modal
  const handleCloseResponseModal = () => {
    setShowResponseModal(false);
    setSelectedResponse(null);
  };

  const getBadgeClass = (type) => {
    switch (type) {
      case "parent_category":
        return "bg-primary";
      case "agent":
        return "bg-secondary";
      case "dark_pattern":
        return "bg-success";
      case "law":
        return "bg-danger";
      case "jurisdiction":
        return "bg-warning";
      case "clause":
        return "bg-info";
      default:
        return "bg-secondary";
    }
  };

  return (
    <Container fluid className="database-page-container">
      <Row className="mb-3">
        <Col>
          <h2>Database Explorer</h2>
          <p>
            Interactive visualization and management of regulatory and reasoning data.
          </p>
          
          {/* Database selector dropdown */}
          <div className="mb-4">
            <Form.Group>
              <Form.Label>Select Database:</Form.Label>
              <Form.Select 
                value={selectedDatabase}
                onChange={(e) => handleDatabaseChange(e.target.value)}
                className="database-selector"
              >
                <option value="regulatory">Regulatory Graph Database</option>
                <option value="reasoning">Reasoning Database</option>
              </Form.Select>
            </Form.Group>
          </div>
        </Col>
      </Row>

      {selectedDatabase === "regulatory" ? (
        // Regulatory Database View
        <Row>
          <Col md={9} className="graph-container">
            {loading ? (
              <div className="loading-spinner">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <ForceGraph2D
                ref={graphRef}
                graphData={filteredData}
                nodeRelSize={6}
                linkWidth={1}
                backgroundColor="#222222"
                nodeCanvasObject={paintNode}
                linkCanvasObject={paintLink}
                onNodeClick={handleNodeClick}
                linkDirectionalParticles={4}
                linkDirectionalParticleWidth={2}
                cooldownTicks={100}
                onEngineStop={() => focusGraph()}
              />
            )}
          </Col>

          <Col md={3}>
            <Card bg="dark" text="white" className="filter-panel">
              <Card.Header>Filter Options</Card.Header>
              <Card.Body>
                <Form>
                  {Object.keys(filters).map(filterType => (
                    <Form.Check 
                      key={filterType}
                      type="checkbox"
                      id={`filter-${filterType}`}
                      label={
                        <span>
                          <span 
                            className="color-dot" 
                            style={{ backgroundColor: NODE_COLORS[filterType] }}
                          ></span>
                          {filterType.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                      }
                      checked={filters[filterType]}
                      onChange={() => handleFilterChange(filterType)}
                    />
                  ))}
                  <Button 
                    variant="outline-light" 
                    size="sm" 
                    className="mt-3"
                    onClick={resetFilters}
                  >
                    Reset Filters
                  </Button>
                  <Button 
                    variant="outline-light" 
                    size="sm" 
                    className="mt-3 ms-2"
                    onClick={focusGraph}
                  >
                    Fit View
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            {selectedNode && (
              <Card bg="dark" text="white" className="node-info mt-3">
                <Card.Header>Node Details</Card.Header>
                <Card.Body>
                  <h5>{selectedNode.label || selectedNode.id}</h5>
                  <p><strong>Type:</strong> {selectedNode.group.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                  <p><strong>Connections:</strong> {highlightLinks.size}</p>
                  <div className="connected-nodes">
                    <strong>Connected to:</strong>
                    <ul className="small-list">
                      {Array.from(highlightNodes).filter(id => id !== selectedNode.id).map(nodeId => {
                        const node = graphData.nodes.find(n => n.id === nodeId);
                        return node ? (
                          <li key={nodeId}>
                            {node.label || node.id} 
                            <span className="node-type">({node.group})</span>
                          </li>
                        ) : null;
                      })}
                    </ul>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      ) : (
        // Reasoning Database View
        <Row>
          <Col md={12}>
            <Card bg="light" className="mb-4">
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Reasoning Database</h4>
                <span className="badge bg-light text-dark">
                  Total Entries: {filteredReasoningData.length} of {reasoningData.length}
                </span>
              </Card.Header>
              <Card.Body>
                {/* Filter dropdowns */}
                <Row className="mb-4">
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label>Filter by Session:</Form.Label>
                      <Form.Select
                        value={sessionFilter}
                        onChange={(e) => setSessionFilter(e.target.value)}
                      >
                        <option value="">All Sessions</option>
                        {availableSessions.map(session => (
                          <option key={session} value={session}>
                            {session}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label>Filter by Journey:</Form.Label>
                      <Form.Select
                        value={journeyFilter}
                        onChange={(e) => setJourneyFilter(e.target.value)}
                      >
                        <option value="">All Journeys</option>
                        {availableJourneys.map(journey => (
                          <option key={journey} value={journey}>
                            {journey}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => {
                        setSessionFilter("");
                        setJourneyFilter("");
                      }}
                      className="mb-3 w-100"
                    >
                      Clear Filters
                    </Button>
                  </Col>
                </Row>

                {/* Reasoning data table */}
                {reasoningLoading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th onClick={() => handleSortChange("page_type")} className="sortable-header">
                            Page Type {sortField === "page_type" && (sortDirection === "asc" ? "↑" : "↓")}
                          </th>
                          <th onClick={() => handleSortChange("page_name")} className="sortable-header">
                            Page Name {sortField === "page_name" && (sortDirection === "asc" ? "↑" : "↓")}
                          </th>
                          <th onClick={() => handleSortChange("timestamp")} className="sortable-header">
                            Timestamp {sortField === "timestamp" && (sortDirection === "asc" ? "↑" : "↓")}
                          </th>
                          <th style={{ width: "150px" }}>Responses</th>
                          <th style={{ width: "100px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredReasoningData.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center py-3">No data found</td>
                          </tr>
                        ) : (
                          filteredReasoningData.map((entry) => (
                            <tr key={entry.id}>
                              <td>{entry.page_type || "Unknown"}</td>
                              <td>{entry.page_name || "Unnamed"}</td>
                              <td>
                                {entry.timestamp instanceof Date 
                                  ? entry.timestamp.toLocaleString() 
                                  : "Unknown"}
                              </td>
                              <td className="text-center">
                                {Array.isArray(entry.response) && entry.response.length > 0 ? (
                                  <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={() => handleViewResponse(entry.response)}
                                  >
                                    View ({entry.response.length})
                                  </Button>
                                ) : (
                                  <span className="text-muted">No data</span>
                                )}
                              </td>
                              <td className="text-center">
                                <Button 
                                  variant="danger" 
                                  size="sm"
                                  onClick={() => handleDeleteEntry(entry.id)}
                                >
                                  Delete
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Response Detail Modal */}
      <Modal
        show={showResponseModal}
        onHide={handleCloseResponseModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Response Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-table-container">
          {selectedResponse && (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th style={{ width: "20%" }}>Type</th>
                  <th style={{ width: "20%" }}>Dark Pattern</th>
                  <th style={{ width: "60%" }}>Content</th>
                </tr>
              </thead>
              <tbody>
                {selectedResponse.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <span className={`badge ${getBadgeClass(item.type || item.kind || "unknown")}`}>
                        {item.type || item.kind || "unknown"}
                      </span>
                    </td>
                    <td>
                      {item.dark_pattern ? (
                        <div>
                          <span className="badge bg-warning text-dark">{item.dark_pattern}</span>
                          {item.parent_type && <div className="small text-muted mt-1">Parent: {item.parent_type}</div>}
                        </div>
                      ) : (
                        <span className="text-muted">None</span>
                      )}
                    </td>
                    <td className="cell-content">
                      <div className="truncate-text">{item.text}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseResponseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DatabasePage; 